import {
  addR,
  divR,
  equalsR,
  intR,
  isZeroR,
  mulR,
  negR,
  RATIONAL_ZERO,
  type Rational,
} from "@/lib/tool-kit/math/rational";

/**
 * Polynomials in several variables with exact rational coefficients.
 *
 * A monomial is keyed by a canonical string — `""` for the constant, `"x"`, `"x^2"`,
 * `"x^2*y"` — with the variables in alphabetical order, so two equal monomials always
 * share a key. The parser builds these, the linear and system solvers read them, and the
 * one-variable tools turn them into coefficient arrays (`uni.ts`).
 */
export type Poly = Record<string, Rational>;

const ONE = intR(1);

export function polyConst(value: Rational): Poly {
  return isZeroR(value) ? {} : { "": value };
}

export function polyVar(name: string): Poly {
  return { [name]: ONE };
}

export function polyIsZero(p: Poly): boolean {
  return Object.keys(p).length === 0;
}

export function polyIsConstant(p: Poly): boolean {
  return Object.keys(p).every((key) => key === "");
}

/** The constant value of a constant polynomial (zero when empty). */
export function polyConstantValue(p: Poly): Rational {
  return p[""] ?? RATIONAL_ZERO;
}

export function polyAdd(a: Poly, b: Poly): Poly {
  const result: Poly = { ...a };
  for (const [key, value] of Object.entries(b)) {
    const sum = addR(result[key] ?? RATIONAL_ZERO, value);
    if (isZeroR(sum)) {
      delete result[key];
    } else {
      result[key] = sum;
    }
  }
  return result;
}

export function polyNeg(a: Poly): Poly {
  const result: Poly = {};
  for (const [key, value] of Object.entries(a)) {
    result[key] = negR(value);
  }
  return result;
}

export function polySub(a: Poly, b: Poly): Poly {
  return polyAdd(a, polyNeg(b));
}

export function exponentsOf(key: string): Record<string, number> {
  const exponents: Record<string, number> = {};
  if (key === "") {
    return exponents;
  }
  for (const part of key.split("*")) {
    const [name, power] = part.split("^");
    exponents[name] = (exponents[name] ?? 0) + (power ? Number(power) : 1);
  }
  return exponents;
}

function keyOf(exponents: Record<string, number>): string {
  return Object.keys(exponents)
    .sort()
    .map((name) =>
      exponents[name] === 1 ? name : `${name}^${exponents[name]}`
    )
    .join("*");
}

function multiplyKeys(a: string, b: string): string {
  if (a === "") {
    return b;
  }
  if (b === "") {
    return a;
  }
  const exponents = exponentsOf(a);
  for (const [name, power] of Object.entries(exponentsOf(b))) {
    exponents[name] = (exponents[name] ?? 0) + power;
  }
  return keyOf(exponents);
}

export function polyMul(a: Poly, b: Poly): Poly {
  let result: Poly = {};
  for (const [keyA, valueA] of Object.entries(a)) {
    for (const [keyB, valueB] of Object.entries(b)) {
      result = polyAdd(result, {
        [multiplyKeys(keyA, keyB)]: mulR(valueA, valueB),
      });
    }
  }
  return result;
}

export function polyScale(a: Poly, factor: Rational): Poly {
  if (isZeroR(factor)) {
    return {};
  }
  const result: Poly = {};
  for (const [key, value] of Object.entries(a)) {
    result[key] = mulR(value, factor);
  }
  return result;
}

export function polyDivideByConstant(a: Poly, divisor: Rational): Poly {
  return polyScale(a, divR(ONE, divisor));
}

/** Total degree of the highest monomial (0 for a constant or zero). */
export function polyTotalDegree(p: Poly): number {
  let best = 0;
  for (const key of Object.keys(p)) {
    const degree = Object.values(exponentsOf(key)).reduce((s, n) => s + n, 0);
    best = Math.max(best, degree);
  }
  return best;
}

/** Variables that occur with a non-zero coefficient, alphabetically. */
export function polyVariables(p: Poly): string[] {
  const names = new Set<string>();
  for (const key of Object.keys(p)) {
    for (const name of Object.keys(exponentsOf(key))) {
      names.add(name);
    }
  }
  return [...names].sort();
}

export function polyEquals(a: Poly, b: Poly): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    const left = a[key];
    const right = b[key];
    if (!(left && right && equalsR(left, right))) {
      return false;
    }
  }
  return true;
}
